import { CarouselContent, CarouselItem } from "hec/components/ui/carousel";
import {
  slidesVisibleValuesDesktop,
  slidesVisibleValuesMobile,
} from "hec/components/ui/carousel.utils";
import type * as React from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import { Blocks } from "@builder.io/sdk-react";

import { defaultSlide } from "./defaultConfig";

interface CustomCarouselContentProps {
  slides?: Record<string, unknown>[];
  slidesVisible?: string;
  showPeek?: boolean;
  showPeekMobile?: boolean;
  slidesVisibleMobile?: string;
  slidePadding?: string;
  slidePaddingMobile?: string;
  builderBlock?: { id: string };
  attributes?: Record<string, unknown>;
}

export const CustomCarouselContent = (props: CustomCarouselContentProps) => {
  const {
    slides,
    slidesVisible,
    showPeek,
    showPeekMobile,
    slidesVisibleMobile,
    slidePadding,
    slidePaddingMobile,
    builderBlock,
    attributes,
  } = props;
  return (
    <CarouselContent
      {...(attributes as React.HTMLAttributes<HTMLDivElement>)}
      data-testid="carouselContent-CarouselContent"
    >
      {slides?.map((slide, index) => (
        <CarouselItem
          key={index}
          slidesVisible={slidesVisible as never}
          slidesVisibleMobile={slidesVisibleMobile as never}
          slidePadding={slidePadding as never}
          showPeek={showPeek}
          showPeekMobile={showPeekMobile}
          slidePaddingMobile={slidePaddingMobile as never}
          data-testid="carouselContent-CarouselItem"
        >
          <Blocks
            path={`component.options.slides.${index}.content`}
            blocks={[slide] as never}
            parent={builderBlock?.id}
            data-testid="carouselContent-Blocks"
          />
        </CarouselItem>
      ))}
    </CarouselContent>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const CarouselContentDefinition: RegisteredComponent = {
  component: CustomCarouselContent,
  name: "v1: CarouselContent",
  canHaveChildren: true,
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    marginLeft: "auto",
    marginRight: "auto",
  },
  inputs: [
    {
      name: "slides",
      type: "list",
      subFields: [
        {
          name: "content",
          type: "uiBlocks",
          defaultValue: Array(3).fill(defaultSlide),
        },
      ],
      defaultValue: Array(6).fill(defaultSlide),
    },
    {
      name: "slidesVisible",
      friendlyName: "Number of Slides Visible",
      type: "string",
      enum: slidesVisibleValuesDesktop,
      defaultValue: "1",
      helperText: "Number of slides visible on devices larger than mobile",
    },
    {
      name: "showPeek",
      type: "boolean",
      defaultValue: false,
      helperText:
        "Select true if you want padding-right removed and for the next card to peek out",
    },
    {
      name: "slidesVisibleMobile",
      friendlyName: "Number of Slides Visible on Mobile",
      type: "string",
      enum: slidesVisibleValuesMobile,
      defaultValue: "1",
      helperText: "Number of slides visible on mobile devices",
    },
    {
      name: "showPeekMobile",
      type: "boolean",
      defaultValue: true,
      helperText:
        "Select true if you want padding-right removed and for the next card to peek out",
    },
    {
      name: "slidePadding",
      friendlyName: "Padding Between Slides",
      type: "string",
      enum: Array.from({ length: 7 }, (_, i) => i.toString()),
      defaultValue: "2",
      helperText: "Padding between slides",
    },
    {
      name: "slidePaddingMobile",
      friendlyName: "Padding Between Slides Mobile",
      type: "string",
      enum: Array.from({ length: 7 }, (_, i) => i.toString()),
      defaultValue: "2",
      helperText: "Padding between slides on mobile",
    },
  ],
};
